/**
 * Created by or_p on 3/17/2017.
 */
import PropTypes from 'prop-types';

import React, {PureComponent} from 'react';
import * as Colors from 'material-ui/styles/colors';
import {ListItem} from 'material-ui/List';
import {IconMenu} from 'material-ui/IconMenu';
import _ from 'lodash';
import {Toolbar, ToolbarGroup, ToolbarSeparator, ToolbarTitle} from 'material-ui/Toolbar';
import IconButton from 'material-ui/IconButton';
import BoldIcon from 'material-ui/svg-icons/editor/format-bold';
import ItalicIcon from 'material-ui/svg-icons/editor/format-italic';
import UnderlineIcon from 'material-ui/svg-icons/editor/format-underlined';
import ListBulletedIcon from 'material-ui/svg-icons/editor/format-list-bulleted';
import ListNumberedIcon from 'material-ui/svg-icons/editor/format-list-numbered';
import ClearIcon from 'material-ui/svg-icons/editor/format-clear';
import TinyMCE from 'react-tinymce';
import Sticky from 'shared/components/Sticky/Sticky';
const classes = require('./TextEditor.scss');

export default class TextEditor extends PureComponent {
  static contextTypes = {
    muiTheme: PropTypes.object.isRequired,
  };

  constructor(props) {
    super(props);
    const {value, initialValue} = props;
    this.state = {
      value: value || initialValue,
      key: 0,
      focused: false,
    };
    this.tinymceConfig = {
      plugins: 'lists advlist',
      toolbar: false,
      menubar: false,
      statusbar: false,
      object_resizing: false,
      skin_url: 'none ',
      inline: true,
      setup: editor => {
        _.defer(() => this.setState({editor}));
      },
    };
  }

  componentDidMount() {
    this.interval = window.setInterval(this.checkForChanges, 200);
  }

  componentWillReceiveProps(nextProps) {
    /*
     * Since TineMCE doesn't allow controlled mode we fore
     * new component when we detecting external value change.
     */
    const {value} = nextProps;
    if (
      this.props.value !== value &&
      value !== this.lastContent &&
      !(_.isEmpty(this.lastContent) && _.isEmpty(value))
    ) {
      this.lastContent = value;
      const {key} = this.state;
      this.setState({value, key: key + 1});
    }
  }
  componentWillUnmount() {
    window.clearInterval(this.interval);
  }

  checkForChanges = () => {
    const {editor, focused} = this.state;
    if (!focused) {
      return;
    }
    if (editor) {
      const content = editor.getContent();
      if (this.lastContent !== content) {
        this.debouncedUpdate(content);
        this.lastContent = content;
      }
    }
  };

  handleEditorChange = () => {
    this.checkForChanges();
  };

  focus = () => {
    const {editor} = this.state;
    if (editor) {
      editor.execCommand('mceFocus', false, editor.id);
    }
  };

  debouncedUpdate = _.debounce(value => {
    const {onChange} = this.props;
    if (onChange) {
      onChange(value);
    }
  }, 150);

  handleFocus = () => {
    this.setState({focused: true});
  };

  handleBlur = () => {
    this.checkForChanges();
    this.setState({focused: false});
  };

  render() {
    const {rich, readOnly, stickWith, id} = this.props;
    const {editor, value, key} = this.state;
    const TOOLBAR_HEIGHT = 48;
    return (
      <div style={{width: '100%'}}>
        {rich && (
          <Sticky stickWith={stickWith}>
            <TextEditorToolbar
              id={`${id}-toolbar`}
              editor={editor}
              style={{height: TOOLBAR_HEIGHT, ..._.pick(this.props, ['transition', 'backgroundColor'])}}
              disabled={readOnly}
            />
          </Sticky>
        )}
        <div style={{padding: 20, zIndex: 1}} className={classes.content}>
          {readOnly ? (
            <div dangerouslySetInnerHTML={{__html: value}} />
          ) : (
            <TinyMCE
              id={id}
              key={key}
              content={value}
              onChange={this.handleEditorChange}
              config={this.tinymceConfig}
              onBlur={this.handleBlur}
              onFocus={this.handleFocus}
            />
          )}
        </div>
      </div>
    );
  }
}

TextEditor.propTypes = {
  initialValue: PropTypes.string,
  rich: PropTypes.bool,
  onChange: PropTypes.func,
  transition: PropTypes.string,
  backgroundColor: PropTypes.string,
  value: PropTypes.string,
  readOnly: PropTypes.bool,
  stickWith: PropTypes.string,
};

const BLOCK_TYPES = [
  {style: 'InsertUnorderedList', icon: <ListBulletedIcon />},
  {style: 'InsertOrderedList', icon: <ListNumberedIcon />},
];

const INLINE_STYLES = [
  {style: 'Bold', icon: <BoldIcon />},
  {style: 'Italic', icon: <ItalicIcon />},
  {style: 'Underline', icon: <UnderlineIcon />},
];

class TextEditorToolbar extends PureComponent {
  toggleStyle = style => {
    const {editor} = this.props;
    editor.execCommand(style);
  };

  toggleListStyle = style => {
    const {editor} = this.props;
    editor.execCommand(style, false, {'list-style-type': 'default'});
  };

  removeAllInlineStyles = () => {
    const {editor} = this.props;
    editor.execCommand('RemoveFormat');
  };

  render() {
    const {style, disabled, id} = this.props;
    return (
      <Toolbar style={{backgroundColor: Colors.grey50, ...style}}>
        <ToolbarGroup className={`${id}-left`}>
          <InlineStyleControls onToggle={this.toggleStyle} disabled={disabled} />
          <BlockStyleControls onToggle={this.toggleListStyle} disabled={disabled} />
        </ToolbarGroup>
        <ToolbarGroup className={`${id}-right`}>
          <TextEditorToolbarButton icon={<ClearIcon />} onToggle={this.removeAllInlineStyles} disabled={disabled} />
        </ToolbarGroup>
      </Toolbar>
    );
  }
}
TextEditorToolbar.defaultProps = {
  style: {},
};

class TextEditorToolbarButton extends PureComponent {
  onToggle = e => {
    const {style} = this.props;
    e.preventDefault();
    this.props.onToggle(style);
  };
  render() {
    const {icon, disabled} = this.props;
    return (
      <IconButton onMouseDown={this.onToggle} disabled={disabled}>
        {icon}
      </IconButton>
    );
  }
}

const BlockStyleControls = props => {
  return (
    <span style={{display: 'flex'}}>
      {BLOCK_TYPES.map(type => (
        <TextEditorToolbarButton
          key={type.style}
          icon={type.icon}
          onToggle={props.onToggle}
          style={type.style}
          disabled={props.disabled}
        />
      ))}
    </span>
  );
};

const InlineStyleControls = props => {
  return (
    <span style={{display: 'flex'}}>
      {INLINE_STYLES.map(type => (
        <TextEditorToolbarButton
          key={type.style}
          icon={type.icon}
          onToggle={props.onToggle}
          style={type.style}
          disabled={props.disabled}
        />
      ))}
    </span>
  );
};
